import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography } from '@mui/material';

import SftGlobalData from '../../library/SftGlobalData';
import Colors from '../../library/Colors';

/**
 * The .fr3 layout of ONE report (report_.repform only — the language
 * variants repformlang2-4 are deliberately not offered here),
 * each with what can be done to it — SoftTouch staff only (the uploads
 * endpoints enforce the same gate):
 *  - STANDARD report (reptype 0): never edited here — "Copy as custom
 *    report" (MyFashionManager "Rapport kopiëren") + preview only
 *  - CUSTOM report (reptype 1):
 *    - Touch-up    : the layout sits in the client's custom dir → designer
 *    - Fetch layout: only in the shared store → copy it into the client dir
 *    - Upload…     : the file is stored under THIS layout's exact name
 *                    (MyFashionManager "Upload CUSTOM form")
 *    A custom report still pointing at a standard (sf_) layout: read-only.
 * .frf layouts are listed as incompatible.
 *
 * Props: api, sftUserId, repid, t, lang, notify, compact, onChanged,
 *        onCopied(result), onPreview (optional: shows a Preview button)
 */
const ReportLayoutActions = (props) => {
    const navigate = useNavigate();
    const t = props.t || ((k, d) => d || k);
    const tx = (key, fallback) => {
        const value = t(key);
        return value && value !== key ? value : fallback;
    };

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const fileRef = useRef(null);
    const uploadTarget = useRef('');

    const notify = (text, ok) => {
        if (props.notify) props.notify(text, 5000, 'top-right', props.theme, ok ? 1 : 2);
    };

    const reload = useCallback(() => {
        if (!props.repid) return;
        setLoading(true);
        SftGlobalData.post('uploads&action=reportLayouts', {
            key: props.api, sftUserId: props.sftUserId, repid: props.repid,
        }).then((response) => {
            setLoading(false);
            if (response?.success) {
                setData(response.data);
                setError('');
            } else {
                setError(response?.message || 'Error');
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.api, props.repid]);

    useEffect(() => { reload(); }, [reload]);

    const fetchLayout = (name) => {
        if (!window.confirm(tx('upl_confirm_fetch',
            'Copy this layout into the client\'s custom reports so it can be edited?') + ' (' + name + ')')) return;
        setBusy(true);
        SftGlobalData.post('uploads&action=importReportForm', {
            key: props.api, sftUserId: props.sftUserId, name,
        }).then((response) => {
            setBusy(false);
            if (response?.success) {
                reload();
                if (props.onChanged) props.onChanged();
            } else {
                notify(response?.message || 'Fetch failed', false);
            }
        });
    };

    // MyFashionManager "Rapport kopiëren": a new custom report (reptype 1)
    // with cm_ copies of the layouts in the client dir.
    const copyAsCustom = () => {
        const current = data?.report?.title || '';
        const title = window.prompt(tx('rla_copy_title',
            'Title of the new custom report (empty = same title):'), current);
        if (title === null) return;
        setBusy(true);
        SftGlobalData.post('uploads&action=copyReport', {
            key: props.api, sftUserId: props.sftUserId, repid: props.repid,
            title: title.trim() === current ? '' : title.trim(),
        }).then((response) => {
            setBusy(false);
            if (response?.success) {
                const result = response.data || {};
                const missing = (result.missing_files || []).length + (result.failed_files || []).length;
                notify(tx('rla_copied', 'Custom report created:') + ' ' + result.repid
                    + (missing ? ' — ' + tx('rla_copy_missing', 'some layout files could not be copied:') + ' '
                        + [...(result.missing_files || []), ...(result.failed_files || [])].join(', ') : ''), !missing);
                if (props.onCopied) props.onCopied(result);
                if (props.onChanged) props.onChanged();
            } else {
                notify(response?.message || 'Copy failed', false);
            }
        });
    };

    const pickUpload = (name) => {
        uploadTarget.current = name;
        if (fileRef.current) fileRef.current.click();
    };

    const doUpload = (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        const name = uploadTarget.current;
        if (!file || !name) return;
        if (!/\.fr3$/i.test(file.name)) {
            notify(tx('upl_fr3_only', 'Only .fr3 files — FastReport 2 (.frf) forms only render on Windows MySales'), false);
            return;
        }
        if (file.name !== name && !window.confirm(
            tx('rla_rename_confirm', 'The file will be stored under the layout name of this report:') + ' ' + name)) {
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
            const bytes = new Uint8Array(ev.target.result);
            let binary = '';
            for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
            setBusy(true);
            SftGlobalData.post('uploads&action=uploadReportLayout', {
                key: props.api, sftUserId: props.sftUserId,
                repid: props.repid, name, content: btoa(binary),
            }).then((response) => {
                setBusy(false);
                if (response?.success) {
                    notify(tx('upl_uploaded', 'Uploaded'), true);
                    reload();
                    if (props.onChanged) props.onChanged();
                } else {
                    notify(response?.message || 'Upload failed', false);
                }
            });
        };
        reader.readAsArrayBuffer(file);
    };

    const btn = (label, onClick, solid) => (
        <Button size="small" variant={solid ? 'contained' : 'outlined'} disabled={busy} onClick={onClick}
            sx={{
                fontSize: 12.5, textTransform: 'none', py: 0.3, px: 1.3, minWidth: 0,
                borderColor: Colors.sft_blue, color: solid ? '#fff' : Colors.sft_blue,
                backgroundColor: solid ? Colors.sft_blue : undefined,
            }}>
            {label}
        </Button>
    );

    if (loading) {
        return <Box sx={{ py: 1 }}><CircularProgress size={18} /></Box>;
    }
    if (error) {
        return <Typography variant="body2" color="error">{error}</Typography>;
    }

    // Only the primary layout (repform) gets buttons; the server still
    // returns the language variants, they are hidden on purpose.
    const forms = (data?.forms || []).filter((form) => !form.lang);
    const custom = !!data?.report?.custom;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {forms.length === 0 && (data?.frf_forms || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    {tx('rla_no_layout', 'This report has no .fr3 layout (grid report).')}
                </Typography>
            )}
            {forms.map((form) => {
                // Editable only for a custom report whose layout is not a
                // standard (sf_) layout - the server enforces the same rule.
                const editable = custom && form.editable !== false;
                return (
                    <Box key={form.name} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 13.5, fontFamily: 'Consolas, monospace' }}>{form.name}</Typography>
                        <Typography sx={{ fontSize: 12, opacity: 0.7 }}>
                            {!editable
                                ? tx('rla_readonly', 'standard layout — read-only')
                                : form.present ? tx('rla_in_client_dir', 'custom (client dir)') : tx('rla_in_shared', 'shared store only')}
                        </Typography>
                        <Box sx={{ flex: 1 }} />
                        {editable && (form.present
                            ? btn(tx('upl_touchup_btn', 'Touch-up'),
                                () => navigate('/' + props.lang + '/mobile/designer/reports/' + encodeURIComponent(form.name)
                                    + '?from=reports' + (props.repid ? '&repid=' + encodeURIComponent(props.repid) : '')))
                            : btn(tx('upl_fetch_layout', 'Fetch layout'), () => fetchLayout(form.name)))}
                        {editable && btn(tx('rla_upload', 'Upload…'), () => pickUpload(form.name))}
                    </Box>
                );
            })}
            {(data?.frf_forms || []).map((name) => (
                <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 13.5, fontFamily: 'Consolas, monospace', opacity: 0.7 }}>{name}</Typography>
                    <Typography sx={{ fontSize: 12, color: Colors.def_red }}>
                        {tx('rla_frf', 'FastReport 2 (.frf) — incompatible, Windows only')}
                    </Typography>
                </Box>
            ))}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {!custom && btn(tx('rla_copy_custom', 'Copy as custom report'), copyAsCustom, true)}
                {props.onPreview && btn(tx('rla_preview', 'Preview'), props.onPreview)}
            </Box>
            {!custom && !props.compact && (
                <Typography sx={{ fontSize: 12, opacity: 0.7 }}>
                    {tx('rla_standard_hint', 'Standard report: it cannot be edited here — copy it as a custom report and edit the copy.')}
                </Typography>
            )}
            <input type="file" accept=".fr3" style={{ display: 'none' }} ref={fileRef} onChange={doUpload} />
        </Box>
    );
};

export default ReportLayoutActions;
